import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { config } from '../config';

// SessionCookieBridge trades the OIDC access token for a pair of cookies
// (sa_session + sa_csrf) on the apex `.sportadmin.io` domain so the
// admin SPA (go.sportadmin.io) and the universal app
// (app.sportadmin.io) share the same browser session. Without this, the
// Settings → App preview iframe would land on the PWA's own Zitadel
// login redirect even though the user is already authenticated here.
//
// Lifecycle:
//   - On every access_token change (initial sign-in + silent renew),
//     POST /api/v1/auth/session with Bearer → server validates JWT,
//     sets the cookies. Idempotent — calling it with the same token
//     more than once just re-issues the same Max-Age.
//   - On sign-out (auth.user becomes null AFTER having been set),
//     DELETE /api/v1/auth/session to clear the cookies. We don't fire
//     it pre-auth (no point) or in E2E mode (cookies aren't part of
//     the static-token testing path).
//
// All requests use credentials:'include' so the cookies actually travel.
// The fetch is best-effort: a network failure here doesn't break the
// admin SPA (it still has its Bearer in sessionStorage), it just means
// the iframe will fall back to Zitadel SSO instead of cookie SSO. We
// log the failure so it's visible if it ever starts happening.
export function SessionCookieBridge() {
  const auth = useAuth();
  const lastSyncedRef = useRef<string | null>(null);
  const wasAuthedRef = useRef(false);

  useEffect(() => {
    // E2E mode never actually talked to Zitadel and the server isn't
    // configured to set cookies under TEST_MODE — skip the dance.
    if (config.e2eDevToken !== null) return;

    const token = auth.user?.access_token ?? null;
    if (token && token !== lastSyncedRef.current) {
      lastSyncedRef.current = token;
      wasAuthedRef.current = true;
      void fetch(`${config.apiURL}/api/v1/auth/session`, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      }).catch((err) => {
        // Don't crash the app — Bearer transport still works.
        console.warn('session cookie bridge POST failed', err);
      });
      return;
    }

    if (!token && wasAuthedRef.current) {
      // Transition from authed → signed out. Clear cookies so the
      // next login can't reuse a stale session. Doesn't require auth
      // on the server side (idempotent clear).
      wasAuthedRef.current = false;
      lastSyncedRef.current = null;
      void fetch(`${config.apiURL}/api/v1/auth/session`, {
        method: 'DELETE',
        credentials: 'include',
      }).catch((err) => {
        console.warn('session cookie bridge DELETE failed', err);
      });
    }
  }, [auth.user?.access_token]);

  return null;
}
